import { ProductVariant } from '../../hooks/useProductVariants'
import { SelectedOption } from '../modifiers/VariationModifiersSection'

/**
 * Checks if a variant matches a specific selected option.
 */
export const variantMatchesOption = (
  variant: ProductVariant,
  selection: SelectedOption
): boolean => {
  return (
    variant.catalogProductInfo?.productVariant?.selectedOptions?.some(
      (opt) =>
        opt.id === selection.optionId && opt.value.id === selection.valueId
    ) ?? false
  )
}

/**
 * Finds variants that match ALL of the given selected options (exact match).
 *
 * @param variants - Array of product variants to search
 * @param selectedOptions - Currently selected option/value pairs
 * @returns Array of variants that match all selected options
 */
export const findExactMatchingVariants = (
  variants: ProductVariant[],
  selectedOptions: SelectedOption[]
): ProductVariant[] => {
  if (!variants.length || !selectedOptions.length) {
    return []
  }

  return variants.filter((variant) => {
    return selectedOptions.every((selectedOption) =>
      variantMatchesOption(variant, selectedOption)
    )
  })
}

/**
 * Resolves which of the current selections belongs to the primary option
 * (the product's first availableOption by sortOrder).
 */
export const findPrimarySelection = (
  selectedOptions: SelectedOption[],
  primaryOptionId?: string
): SelectedOption | undefined =>
  primaryOptionId
    ? selectedOptions.find((opt) => opt.optionId === primaryOptionId)
    : undefined

/**
 * Finds the best matching variant for the given selected options.
 * Returns the variant matching the most options, with tiebreakers:
 * 1. Higher match count wins
 * 2. Matching the primary option (first by sortOrder) wins
 * 3. Having images wins
 *
 * @param variants - Array of product variants to search
 * @param selectedOptions - Currently selected option/value pairs
 * @param primarySelection - The resolved primary selection for tiebreaking
 * @returns The best matching variant, or null if no matches found
 */
export const findBestMatchingVariant = (
  variants: ProductVariant[],
  selectedOptions: SelectedOption[],
  primarySelection?: SelectedOption
): ProductVariant | null => {
  if (!variants.length || !selectedOptions.length) {
    return null
  }

  const matchedVariants = variants
    .map((variant) => ({
      variant,
      matchCount: selectedOptions.filter((opt) =>
        variantMatchesOption(variant, opt)
      ).length,
      matchesPrimary: primarySelection
        ? variantMatchesOption(variant, primarySelection)
        : false,
      hasImages: variantHasImages(variant)
    }))
    .filter((scored) => scored.matchCount > 0)

  if (matchedVariants.length === 0) {
    return null
  }

  matchedVariants.sort((a, b) => {
    if (a.matchCount !== b.matchCount) return b.matchCount - a.matchCount
    if (a.matchesPrimary !== b.matchesPrimary) return a.matchesPrimary ? -1 : 1
    if (a.hasImages !== b.hasImages) return a.hasImages ? -1 : 1
    return 0
  })

  return matchedVariants[0]?.variant ?? null
}

/**
 * Finds a variant matching the given option that has images.
 * Used as an image fallback when the exact variant has no images.
 *
 * Example: User selects red+small, red-small has no images,
 * but red-medium does → returns red-medium for image display.
 */
export const findVariantWithImagesForOption = (
  variants: ProductVariant[],
  option: SelectedOption
): ProductVariant | null =>
  variants.find(
    (v) => variantMatchesOption(v, option) && variantHasImages(v)
  ) ?? null

/**
 * Checks if a variant has any images (either imageUrls or imageSetUrls).
 */
export const variantHasImages = (variant: ProductVariant): boolean => {
  return (
    (variant.imageSetUrls && variant.imageSetUrls.length > 0) ||
    variant.imageUrls != null
  )
}

type ImageSource = Pick<ProductVariant, 'imageUrls' | 'imageSetUrls'>

type ResolvedImages = {
  imageUrls: ProductVariant['imageUrls']
  imageSetUrls: ProductVariant['imageSetUrls']
}

/**
 * Resolves which images to display from an ordered list of sources.
 * Each source is tried in priority order; empty arrays are treated as absent.
 *
 * imageSetUrls: first non-empty imageSetUrls from the sources
 * imageUrls: first non-null imageUrls from the sources, falling back to imageSetUrls[0]
 *
 * @param sources - Image sources in priority order (e.g., [variant, product, firstVariant])
 */
export const resolveImages = (
  sources: (ImageSource | null | undefined)[]
): ResolvedImages => {
  const imageSetUrls =
    sources.find((s) => s?.imageSetUrls && s.imageSetUrls.length > 0)
      ?.imageSetUrls ?? null

  const imageUrls =
    sources.find((s) => s?.imageUrls != null)?.imageUrls ??
    imageSetUrls?.[0] ??
    null

  return { imageUrls, imageSetUrls }
}
